<template>
  <div class="fade-in interview">
    <div class="columns">
      <div class="column is-full">
        <h1 class="title">
          <span v-if="interview.status === 'published'">Published:</span>
          <span v-if="interview.status === 'draft'">Edit:</span>

          <span v-if="editName">
            <input type="text" class="input" v-model="interviewName" />
            <button @click="updateName" class="button update is-info is-small">
              Update
            </button>
            <button
              @click="cancelUpdateName"
              class="button cancel is-info is-small"
            >
              Cancel
            </button>
          </span>
          <span v-else>
            {{ interview.name }}
            <a href="#" class="edit-name" @click="triggerEditName">
              <i class="fa fa-pencil fa-small mr1"></i>
            </a>
          </span>
        </h1>
        <h2 v-if="!canEdit()">
          <small class="mr2">
            <i class="fa fa-mobile-phone fa-small"></i>
            <a :title="interview.scheduleMessage">{{ interview.keyword }}</a>
          </small>
          <small>
            <i class="fa fa-link fa-small"></i>
            <a :href="scheduleUrl(interview.keyword)" target="_blank">{{
              scheduleUrl(interview.keyword)
            }}</a>
          </small>
        </h2>

        <div class="tabs is-large mt2">
          <ul>
            <li :class="[{ 'is-active': tab === 'questions' }]">
              <a @click="setTab('questions')">Questions</a>
            </li>
            <li :class="[{ 'is-active': tab === 'messaging' }]">
              <a @click="setTab('messaging')">Messaging</a>
            </li>
            <li :class="[{ 'is-active': tab === 'notifications' }]">
              <a @click="setTab('notifications')">Notifications</a>
            </li>
            <li :class="[{ 'is-active': tab === 'settings' }]">
              <a @click="setTab('settings')">Settings</a>
            </li>
          </ul>
        </div>

        <interview-buttons
          v-if="tab === 'questions'"
          :published="published"
          :cloneLoading="cloneLoading"
          :loading="loading"
          :canEdit="canEdit()"
          :showPublishButton="showPublishButton()"
          @clone="cloneInterview"
          @edit="editInterview()"
          @publish="publish"
          @cancel="cancelInterviewEdit"
        ></interview-buttons>
      </div>
      <div class="is-clearfix"></div>
    </div>

    <div v-if="tab === 'questions'">
      <section
        :class="sectionClasses('')"
        :key="page.id"
        v-for="page in Object.values(interview.pages)"
      >
        <div class="level new-page">
          <div class="level-left">
            <div class="level-item">
              <h1 class="subtitle">Page: {{ page.attributes.title }}</h1>
            </div>
          </div>
          <div class="level-right" v-if="canEdit()">
            <div class="level-item">
              <div class="dropdown is-right is-hoverable">
                <div class="dropdown-trigger">
                  <button class="button is-info" type="button">
                    <span>Actions</span>
                    <span class="icon is-small">
                      <i class="fa fa-angle-down"></i>
                    </span>
                  </button>
                </div>
                <div class="dropdown-menu" id="dropdown-menu2" role="menu">
                  <div class="dropdown-content has-text-right">
                    <div class="dropdown-item">
                      <button
                        type="button"
                        class="button is-link"
                        @click="pageLogicTrigger(page.id)"
                      >
                        Logic
                      </button>
                    </div>
                    <div class="dropdown-item">
                      <button
                        type="button"
                        class="button is-link"
                        @click="showEditPageTrigger(page.id)"
                      >
                        Edit page
                      </button>
                    </div>
                    <div class="dropdown-item">
                      <button
                        type="button"
                        class="button is-link"
                        @click="deletePage(page.id)"
                      >
                        Delete page
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!showPageEdit(page.id) && !showPageLogic(page.id)">
          <div
            class="question"
            :key="question.id"
            v-for="question in page.attributes.questions"
          >
            <multichoice
              v-if="question.kind === 'multichoice'"
              :question="question"
              :canEdit="canEdit()"
            ></multichoice>
            <casegrid
              v-if="question.kind === 'casegrid'"
              :question="question"
              :canEdit="canEdit()"
            ></casegrid>
            <VisuwellUser
              v-if="question.kind === 'visuwell_user'"
              :question="question"
              :canEdit="canEdit()"
            ></VisuwellUser>
            <ondemand
              v-if="question.kind === 'ondemand'"
              :question="question"
              :canEdit="canEdit()"
            ></ondemand>
            <AsyncCase
              v-if="question.kind === 'async_case'"
              :question="question"
              :canEdit="canEdit()"
            ></AsyncCase>
            <multitext
              v-if="question.kind === 'multitext'"
              :question="question"
              :canEdit="canEdit()"
            ></multitext>
            <date
              v-if="question.kind === 'date'"
              :question="question"
              :canEdit="canEdit()"
            ></date>
            <email
              v-if="question.kind === 'email'"
              :question="question"
              :canEdit="canEdit()"
            ></email>
            <phone
              v-if="question.kind === 'phone'"
              :question="question"
              :canEdit="canEdit()"
            ></phone>
            <location
              v-if="question.kind === 'location'"
              :question="question"
              :canEdit="canEdit()"
            ></location>
            <dropdown
              v-if="question.kind === 'dropdown'"
              :question="question"
              :canEdit="canEdit()"
            ></dropdown>
            <schedule
              v-if="question.kind === 'schedule'"
              :question="question"
              :canEdit="canEdit()"
            ></schedule>
            <zip
              v-if="question.kind === 'zip'"
              :question="question"
              :canEdit="canEdit()"
            ></zip>
            <single-text
              v-if="question.kind === 'singletext'"
              :question="question"
              :canEdit="canEdit()"
            ></single-text>
            <checkbox
              v-if="question.kind === 'checkbox'"
              :question="question"
              :canEdit="canEdit()"
            ></checkbox>
            <file
              v-if="question.kind === 'file'"
              :question="question"
              :canEdit="canEdit()"
            ></file>
            <stripe
              v-if="question.kind === 'stripe'"
              :question="question"
              :canEdit="canEdit()"
            ></stripe>
            <instruction
              v-if="question.kind === 'instruction'"
              :question="question"
              :canEdit="canEdit()"
            ></instruction>
            <VideoInstruction
              v-if="question.kind === 'video'"
              :question="question"
              :canEdit="canEdit()"
            ></VideoInstruction>
          </div>

          <div class="mt2 has-text-centered" v-if="canEdit()">
            <button
              type="button"
              class="button is-info is-small"
              @click="showAddQuestion(page.id)"
            >
              Add Question
            </button>
          </div>

          <div>
            <h3>Page Logic:</h3>
            <p v-if="getSkipToPage(page)">
              Skip To: {{ getSkipToPage(page).attributes.title }} [page]
            </p>
            <p v-else-if="getSkipToAlternateEnding(page)">
              Skip To:
              {{
                getSkipToAlternateEnding(page).attributes.alternateEndingHeader
              }}
              [alternate ending]
            </p>
            <p v-else-if="getSkipToSummary(page)">Skip To: Review and Submit</p>
            <p v-else>N/A</p>
          </div>
        </div>

        <page-edit
          v-if="showPageEdit(page.id)"
          :page="page"
          :interviewId="interview.id"
          @hidePage="hidePageEdit"
        ></page-edit>
        <page-logic
          v-if="showPageLogic(page.id)"
          :page="page"
          :interviewId="interview.id"
          @hide="pageLogicTrigger(page.id)"
        ></page-logic>
      </section>

      <div class="mv1 has-text-centered" v-if="canEdit()">
        <button type="button" class="button is-link" @click="addPage">
          Add Page
        </button>
      </div>

      <div v-if="interview.alternateEndings">
        <div class="level-left">
          <h2>Alternate Endings:</h2>
        </div>

        <section
          :class="sectionClasses('')"
          :key="ending.id"
          v-for="ending in Object.values(interview.alternateEndings)"
        >
          <alternate-ending
            :interview="interview"
            :canEdit="canEdit()"
            :endingId="ending.id"
          ></alternate-ending>
        </section>
      </div>

      <div class="mv1 has-text-centered" v-if="canEdit()">
        <button
          type="button"
          class="button is-link"
          @click="addAlternateEnding"
        >
          Add Ending
        </button>
      </div>

      <interview-buttons
        :published="published"
        :cloneLoading="cloneLoading"
        :loading="loading"
        :canEdit="canEdit()"
        :showPublishButton="showPublishButton()"
        @clone="cloneInterview"
        @edit="editInterview()"
        @publish="publish"
        @cancel="cancelInterviewEdit"
      ></interview-buttons>

      <add-question :page="page"></add-question>
    </div>

    <div v-if="tab === 'messaging'">
      <section :class="sectionClasses('mt3')">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h1 class="subtitle">Invitation Message (SMS)</h1>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div v-if="!editSchedule">
                <button
                  class="button is-info is-small"
                  @click="toggleEditSchedule"
                >
                  Edit
                </button>
              </div>
              <div v-if="editSchedule">
                <button
                  :class="[
                    'button',
                    'is-primary',
                    'is-small',
                    { 'is-loading': loading }
                  ]"
                  @click="saveSchedule"
                >
                  Update
                </button>
                <button class="button is-small" @click="toggleEditSchedule">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="question" v-if="!editSchedule">
          <pre>{{ interview.scheduleMessage }}</pre>
        </div>

        <div class="question" v-if="editSchedule">
          <textarea
            class="textarea"
            v-model="scheduleMessage"
            maxlength="140"
          ></textarea>
          <div class="is-pulled-right">{{ scheduleMessageCount }} / 140</div>
        </div>
      </section>

      <section :class="sectionClasses('mt3')">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h1 class="subtitle">Welcome Header/Message</h1>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div v-if="!editWelcome">
                <button
                  class="button is-info is-small"
                  @click="toggleEditWelcome"
                >
                  Edit
                </button>
              </div>
              <div v-if="editWelcome">
                <button
                  :class="[
                    'button',
                    'is-primary',
                    'is-small',
                    { 'is-loading': loadingWelcome }
                  ]"
                  @click="saveWelcome"
                >
                  Update
                </button>
                <button class="button is-small" @click="toggleEditWelcome">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="question" v-if="!editWelcome">
          <h3>{{ interview.welcomeHeader }}</h3>
          <span v-html="interview.welcomeMessage"></span>
        </div>

        <div class="question" v-if="editWelcome">
          <div class="field">
            <label class="label">Header</label>
            <div class="control mt-2">
              <input type="text" class="input" v-model="welcomeHeader" />
            </div>
          </div>

          <div class="field">
            <label class="label">Message</label>
            <div class="control mt-2">
              <quill-editor
                v-model="welcomeMessage"
                :options="welcomeMessageEditorOptions"
              ></quill-editor>
            </div>
          </div>
        </div>
      </section>

      <section :class="sectionClasses('mt3')">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h1 class="subtitle">User Token Expired Message</h1>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div v-if="!editUserTokenExpiredMessage">
                <button
                  class="button is-info is-small"
                  @click="toggleEditUserTokenExpiredMessage"
                >
                  Edit
                </button>
              </div>
              <div v-if="editUserTokenExpiredMessage">
                <button
                  :class="[
                    'button',
                    'is-primary',
                    'is-small',
                    { 'is-loading': loadingUserTokenExpiredMessage }
                  ]"
                  @click="saveUserTokenExpiredMessage"
                >
                  Update
                </button>
                <button
                  class="button is-small"
                  @click="toggleEditUserTokenExpiredMessage"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="question" v-if="!editUserTokenExpiredMessage">
          <span v-html="interview.userTokenExpiredMessage"></span>
        </div>

        <div class="question" v-if="editUserTokenExpiredMessage">
          <div class="field">
            <label class="label">Message</label>
            <div class="control mt-2">
              <quill-editor
                v-model="userTokenExpiredMessage"
                :options="userTokenExpiredMessageEditorOptions"
              ></quill-editor>
            </div>
          </div>
        </div>
      </section>

      <section :class="sectionClasses('mt3')">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h1 class="subtitle">Next Button</h1>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div v-if="!editNextButton">
                <button
                  class="button is-info is-small"
                  @click="toggleEditNextButton"
                >
                  Edit
                </button>
              </div>
              <div v-if="editNextButton">
                <button
                  :class="[
                    'button',
                    'is-primary',
                    'is-small',
                    { 'is-loading': loadingNextButton }
                  ]"
                  @click="saveNextButton"
                >
                  Update
                </button>
                <button class="button is-small" @click="toggleEditNextButton">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="question" v-if="!editNextButton">
          <p>
            <span v-if="interview.nextButtonText">{{
              interview.nextButtonText
            }}</span>
            <span v-else>Next Question</span>
          </p>
        </div>

        <div class="question" v-if="editNextButton">
          <input
            type="text"
            class="input"
            placeholder="Next Question"
            v-model="nextButtonText"
          />
        </div>
      </section>

      <section :class="sectionClasses('mt3')">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h1 class="subtitle">Interview Button</h1>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div v-if="!editButton">
                <button
                  class="button is-info is-small"
                  @click="toggleEditButton"
                >
                  Edit
                </button>
              </div>
              <div v-if="editButton">
                <button
                  :class="[
                    'button',
                    'is-primary',
                    'is-small',
                    { 'is-loading': loadingButton }
                  ]"
                  @click="saveButton"
                >
                  Update
                </button>
                <button class="button is-small" @click="toggleEditButton">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="question" v-if="!editButton">
          <p>
            <i :class="['icon', interview.startButtonIcon]"></i>
            {{ interview.startButtonText }}
          </p>
        </div>

        <div class="question" v-if="editButton">
          <div class="columns">
            <div class="column select edit-button-icon">
              <select v-model="startButtonIcon">
                <option value="icon-calendar">Calendar</option>
                <option value="icon-clipboard-pencil">Clipboard</option>
              </select>
            </div>
            <div class="column control">
              <input type="text" class="input" v-model="startButtonText" />
            </div>
          </div>
        </div>
      </section>

      <section :class="sectionClasses('mt3')">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h1 class="subtitle">Next Steps Header/Message/Button</h1>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div v-if="!editNextSteps">
                <button
                  class="button is-info is-small"
                  @click="toggleEditNextSteps"
                >
                  Edit
                </button>
              </div>
              <div v-if="editNextSteps">
                <button
                  :class="[
                    'button',
                    'is-primary',
                    'is-small',
                    { 'is-loading': loadingNextSteps }
                  ]"
                  @click="saveNextSteps"
                >
                  Update
                </button>
                <button class="button is-small" @click="toggleEditNextSteps">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="question" v-if="!editNextSteps">
          <h3>{{ interview.nextStepsHeader }}</h3>
          <p>
            <span v-html="interview.nextSteps"></span>
          </p>
          <p
            v-if="
              interview.nextStepsButtonLabel && interview.nextStepsButtonUrl
            "
          >
            <a :href="interview.nextStepsButtonUrl">{{
              interview.nextStepsButtonLabel
            }}</a>
          </p>
        </div>

        <div class="question" v-if="editNextSteps">
          <div class="field">
            <label class="label">Header</label>
            <div class="control mt-2">
              <input type="text" class="input" v-model="nextStepsHeader" />
            </div>
          </div>

          <div class="field">
            <label class="label">Message</label>
            <div class="control mt-2">
              <quill-editor
                v-model="nextSteps"
                :options="nextStepsEditorOptions"
              ></quill-editor>
            </div>
          </div>

          <a @click="toggleCallToActionButton" class="button">
            <span v-if="!showCallToActionButton"
              >Add Call To Action Button</span
            >
            <span v-else>Remove Call To Action Button</span>
          </a>

          <div class="add-call-to-action-button" v-if="showCallToActionButton">
            <br />
            <h3>Call To Action Button</h3>
            <div class="field">
              <label class="label">Button Text</label>
              <div class="control mt-2">
                <input
                  type="text"
                  class="input"
                  v-model="nextStepsButtonLabel"
                />
              </div>
            </div>
            <div class="field">
              <label class="label">Button Link</label>
              <div class="control mt-2">
                <input type="text" class="input" v-model="nextStepsButtonUrl" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div v-if="tab === 'notifications'">
      <section :class="sectionClasses('mt3')">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h1 class="subtitle">Notification Email</h1>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div v-if="!editNotificationEmail">
                <button
                  class="button is-info is-small"
                  @click="toggleEditNotificationEmail"
                >
                  Edit
                </button>
              </div>
              <div v-if="editNotificationEmail">
                <button
                  :class="[
                    'button',
                    'is-primary',
                    'is-small',
                    { 'is-loading': loadingNotificationEmail }
                  ]"
                  @click="saveNotificationEmail"
                >
                  Update
                </button>
                <button
                  class="button is-small"
                  @click="toggleEditNotificationEmail"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="question" v-if="!editNotificationEmail">
          <h3>{{ interview.notificationEmailSubject }}</h3>
          <p>{{ interview.notificationEmailBody }}</p>
        </div>

        <div class="question" v-if="editNotificationEmail">
          <div class="field">
            <label class="label">Subject</label>
            <div class="control mt-2">
              <input
                type="text"
                class="input"
                v-model="notificationEmailSubject"
              />
            </div>
          </div>

          <div class="field">
            <label class="label">Body</label>
            <p class="small">
              Template variables:
              <code v-pre
                >{{ patient.first_name }} {{ interview.name }}
                {{ practice.name }} {{ logo_url }} {{ admin_patient_url }}
                {{ admin_patients_url }}</code
              >
            </p>
            <div class="control mt-2">
              <textarea
                class="input"
                v-model="notificationEmailBody"
              ></textarea>
            </div>
          </div>
        </div>
      </section>

      <section :class="sectionClasses('mt3')">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h1 class="subtitle">Notification SMS</h1>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div v-if="!editNotificationText">
                <button
                  class="button is-info is-small"
                  @click="toggleEditNotificationText"
                >
                  Edit
                </button>
              </div>
              <div v-if="editNotificationText">
                <button
                  :class="[
                    'button',
                    'is-primary',
                    'is-small',
                    { 'is-loading': loadingNotificationText }
                  ]"
                  @click="saveNotificationText"
                >
                  Update
                </button>
                <button
                  class="button is-small"
                  @click="toggleEditNotificationText"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="question" v-if="!editNotificationText">
          <pre>{{ interview.notificationText }}</pre>
        </div>

        <div class="question" v-if="editNotificationText">
          <div class="field">
            <label class="label">Message</label>
            <p class="small">
              Template variables:
              <code v-pre
                >{{ patient.first_name }} {{ interview.name }}
                {{ practice.name }} {{ logo_url }} {{ admin_patient_url }}
                {{ admin_patients_url }}</code
              >
            </p>
            <div class="control mt-2">
              <textarea class="input" v-model="notificationText"></textarea>
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="interview.kind === 'Referral_Provider'"
        :class="sectionClasses('mt3')"
      >
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h1 class="subtitle">Referral Email</h1>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div v-if="!editReferralEmail">
                <button
                  class="button is-info is-small"
                  @click="toggleEditReferralEmail"
                >
                  Edit
                </button>
              </div>
              <div v-if="editReferralEmail">
                <button
                  :class="[
                    'button',
                    'is-primary',
                    'is-small',
                    { 'is-loading': loadingReferralEmail }
                  ]"
                  @click="saveReferralEmail"
                >
                  Update
                </button>
                <button
                  class="button is-small"
                  @click="toggleEditReferralEmail"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="question" v-if="!editReferralEmail">
          <h3>{{ interview.notificationReferrerSubject }}</h3>
          <p>{{ interview.notificationReferrerBody }}</p>
        </div>

        <div class="question" v-if="editReferralEmail">
          <div class="field">
            <label class="label">Subject</label>
            <div class="control mt-2">
              <input
                type="text"
                class="input"
                v-model="notificationReferrerSubject"
              />
            </div>
          </div>

          <div class="field">
            <label class="label">Body</label>
            <div class="control mt-2">
              <textarea
                class="input"
                v-model="notificationReferrerBody"
              ></textarea>
            </div>
          </div>
        </div>
      </section>

      <section :class="sectionClasses('mt3')">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h1 class="subtitle">Notification Users</h1>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div v-if="!editNotificationUsers">
                <button
                  class="button is-info is-small"
                  @click="toggleEditNotificationUsers"
                >
                  Edit
                </button>
              </div>
              <div v-if="editNotificationUsers">
                <button
                  :class="[
                    'button',
                    'is-primary',
                    'is-small',
                    { 'is-loading': loadingNotificationUsers }
                  ]"
                  @click="saveNotificationUsers"
                >
                  Update
                </button>
                <button
                  class="button is-small"
                  @click="toggleEditNotificationUsers"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="question" v-if="!editNotificationUsers">
          <ul>
            <li v-for="user in displayNotificationUsers">{{ user.email }}</li>
          </ul>
        </div>

        <div class="question" v-if="editNotificationUsers">
          <label for="notificationUsers">
            Select users you that want to receive notifications for this
            interview:
          </label>
          <multiselect
            name="notificationUsers"
            v-model="selectedNotificationUsers"
            track-by="id"
            label="email"
            placeholder="Select one"
            :options="notificationUsersOptions"
            :multiple="true"
          ></multiselect>
        </div>
      </section>
    </div>

    <div v-if="tab === 'settings'">
      <section :class="sectionClasses('mt3')">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h1 class="subtitle">On interview completion</h1>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div v-if="!editOnCompletion">
                <button
                  class="button is-info is-small"
                  @click="toggleEditOnCompletion"
                >
                  Edit
                </button>
              </div>
              <div v-if="editOnCompletion">
                <button
                  :class="[
                    'button',
                    'is-primary',
                    'is-small',
                    { 'is-loading': loadingOnCompletion }
                  ]"
                  @click="saveOnCompletion"
                >
                  Update
                </button>
                <button class="button is-small" @click="toggleEditOnCompletion">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="question" v-if="!editOnCompletion">
          <p v-if="interview.submitToVisuwellOnCompletion">
            Submit to Visuwell
          </p>
          <p v-else>Do not submit to Visuwell</p>

          <p v-if="interview.downSampleImagesWhenSendingToVisuwell">
            Down sample images when sending to Visuwell
          </p>
          <p v-else>Do not down sample images when sending to Visuwell</p>
        </div>

        <div class="question" v-if="editOnCompletion">
          <div class="field">
            <label class="label"></label>
            <div class="control mt-2">
              <label class="checkbox" for="submitToVisuwellOnCompletion">
                <input
                  type="checkbox"
                  id="submitToVisuwellOnCompletion"
                  v-model="submitToVisuwellOnCompletion"
                />
                Submit to Visuwell
              </label>
            </div>
          </div>

          <div class="field">
            <label class="label"></label>
            <div class="control mt-2">
              <label
                class="checkbox"
                for="downSampleImagesWhenSendingToVisuwell"
              >
                <input
                  type="checkbox"
                  id="downSampleImagesWhenSendingToVisuwell"
                  v-model="downSampleImagesWhenSendingToVisuwell"
                />
                Down sample images when sending to Visuwell
              </label>
            </div>
          </div>
        </div>

        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <h1 class="subtitle">Require User Token in URL?</h1>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div v-if="!editRequireUserToken">
                <button
                  class="button is-info is-small"
                  @click="toggleEditRequireUserToken"
                >
                  Edit
                </button>
              </div>
              <div v-if="editRequireUserToken">
                <button
                  :class="[
                    'button',
                    'is-primary',
                    'is-small',
                    { 'is-loading': loadingRequireUserToken }
                  ]"
                  @click="saveRequireUserToken"
                >
                  Update
                </button>
                <button
                  class="button is-small"
                  @click="toggleEditRequireUserToken"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="question" v-if="!editRequireUserToken">
          <p v-if="interview.requireUserToken">Required</p>
          <p v-else>Not Required</p>
        </div>

        <div class="question" v-if="editRequireUserToken">
          <div class="field">
            <label class="label"></label>
            <div class="control mt-2">
              <label class="checkbox" for="requireUserToken">
                <input
                  type="checkbox"
                  id="requireUserToken"
                  v-model="requireUserToken"
                />
                Require?
              </label>
            </div>
          </div>
        </div>
      </section>
    </div>

    <vue-toastr ref="toastr"></vue-toastr>
  </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import Multiselect from "vue-multiselect";
import InterviewsService from "@/services/InterviewsService";
import PracticesService from "@/services/PracticesService";
import AlternateEnding from "@/components/alternate_endings/AlternateEnding";
import multichoice from "@/components/questions/multichoice";
import casegrid from "@/components/questions/casegrid";
import ondemand from "@/components/questions/ondemand";
import AsyncCase from "@/components/questions/AsyncCase";
import multitext from "@/components/questions/multitext";
import date from "@/components/questions/date";
import email from "@/components/questions/email";
import phone from "@/components/questions/phone";
import location from "@/components/questions/location";
import dropdown from "@/components/questions/dropdown";
import schedule from "@/components/questions/schedule";
import zip from "@/components/questions/zip";
import singleText from "@/components/questions/singleText";
import checkbox from "@/components/questions/checkbox";
import file from "@/components/questions/file";
import stripe from "@/components/questions/stripe";
import instruction from "@/components/questions/instruction";
import VideoInstruction from "@/components/questions/VideoInstruction";
import addQuestion from "@/components/questions/AddQuestion";
import PageEdit from "@/components/questions/PageEdit";
import PageLogic from "@/components/questions/PageLogic";
import InterviewButtons from "@/components/interview/InterviewButtons";
import VisuwellUser from "@/components/questions/VisuwellUser";
import { quillEditor } from "vue-quill-editor";
import "../../../node_modules/quill/dist/quill.snow.css";

export default {
  name: "interview-edit",

  components: {
    InterviewButtons,
    multichoice,
    casegrid,
    VisuwellUser,
    ondemand,
    AsyncCase, 
    multitext,
    date,
    email,
    phone,
    location,
    dropdown,
    schedule,
    zip,
    checkbox,
    file,
    stripe,
    instruction,
    VideoInstruction,
    singleText,
    addQuestion,
    PageEdit,
    PageLogic,
    quillEditor,
    AlternateEnding,
    Multiselect
  },

  props: ["id"],

  data() {
    return {
      editNotificationUsers: false,
      loadingNotificationUsers: false,
      selectedNotificationUsers: [],
      notificationUsers: [],
      shouldShowCallToActionButton: false,
      loading: false,
      editName: false,
      interviewName: "",
      pageEdit: null,
      pageLogic: null,
      page: { attributes: { title: null } },
      editNextSteps: false,
      nextSteps: null,
      nextStepsHeader: null,
      nextStepsButtonLabel: null,
      nextStepsButtonUrl: null,
      loadingNextSteps: false,
      cloneLoading: false,
      editNextButton: false,
      loadingNextButton: false,
      editButton: false,
      loadingButton: false,
      startButtonIcon: "",
      startButtonText: "",
      welcomeHeader: "",
      welcomeMessage: "",
      editWelcome: false,
      loadingWelcome: false,
      userTokenExpiredMessage: "",
      editUserTokenExpiredMessage: false,
      loadingUserTokenExpiredMessage: false,
      scheduleMessage: "",
      editSchedule: false,
      loadingSchedule: false,
      scheduleMessageCount: 0,
      tab: "questions",
      notificationEmailSubject: "",
      notificationEmailBody: "",
      notificationText: "",
      notificationReferrerSubject: "",
      notificationReferrerBody: "",
      loadingNotificationEmail: false,
      editNotificationEmail: false,
      loadingNotificationText: false,
      editNotificationText: false,
      loadingReferralEmail: false,
      editReferralEmail: false,
      editOnCompletion: false,
      loadingOnCompletion: false,
      submitToVisuwellOnCompletion: 0,
      editRequireUserToken: false,
      loadingRequireUserToken: false,
      requireUserToken: 0,
      downSampleImagesWhenSendingToVisuwell: 0,
      nextStepsEditorOptions: {
        modules: {
          toolbar: [
            [{ header: [1, 2, false] }],
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"]
          ]
        },
        theme: "snow",
        placeholder: "Next steps..."
      },
      welcomeMessageEditorOptions: {
        modules: {
          toolbar: [
            [{ header: [1, 2, false] }],
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"]
          ]
        },
        theme: "snow",
        placeholder: "Welcome message..."
      },
      userTokenExpiredMessageEditorOptions: {
        modules: {
          toolbar: [
            [{ header: [1, 2, false] }],
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"]
          ]
        },
        theme: "snow",
        placeholder: "Message..."
      }
    };
  },

  computed: {
    published() {
      return moment(this.interview.publishedAt).format("lll");
    },
    interview() {
      return this.$store.state.interview;
    },
    practice() {
      return this.$store.getters["practice/current"];
    },
    showCallToActionButton() {
      if (this.shouldShowCallToActionButton == true) {
        return true;
      } else if (this.nextStepsButtonLabel && this.nextStepsButtonUrl) {
        this.shouldShowCallToActionButton = true;
        return true;
      } else {
        return false;
      }
    },
    displayNotificationUsers() {
      let users = this.$store.getters["practice/users"];
      let notificationUsers = this.notificationUsers;
      if (users) {
        users = _.reduce(
          users,
          (collection, user) => {
            if (_.indexOf(notificationUsers, user.id) != -1)
              collection.push({
                id: user.id,
                ...user.attributes
              });
            return collection;
          },
          []
        );
      }
      return users ? users : [];
    },
    notificationUsersOptions() {
      let users = this.$store.getters["practice/users"];
      if (users) {
        users = _.reduce(
          users,
          function(collection, user) {
            collection.push({
              id: user.id,
              ...user.attributes
            });
            return collection;
          },
          []
        );
      }
      return users ? users : [];
    }
  },

  methods: {
    getSkipToSummary(page) {
      let pageId = _.get(page, "attributes.logic.skipTo");
      let skipToSummary = null;
      if (pageId == "summary") {
        skipToSummary = "summary";
      }
      return skipToSummary;
    },

    getSkipToPage(page) {
      let pageId = _.get(page, "attributes.logic.skipTo");
      let skipToPage = null;
      if (pageId) {
        skipToPage = this.$store.getters["interview/getPage"](pageId);
      }
      return skipToPage;
    },

    getSkipToAlternateEnding(page) {
      let skipToAlternateEnding = null;
      let alternateEndingId = _.get(page, "attributes.logic.skipTo");
      if (alternateEndingId) {
        skipToAlternateEnding = this.$store.getters[
          "interview/getAlternateEnding"
        ](alternateEndingId);
      }
      return skipToAlternateEnding;
    },

    toggleEditNotificationUsers() {
      this.selectedNotificationUsers = this.displayNotificationUsers;
      this.editNotificationUsers = !this.editNotificationUsers;
    },
    saveNotificationUsers() {
      this.loadingNotificationUsers = true;
      let selectedNotificationUsers = this.selectedNotificationUsers || [];
      let notificationUsers = _.reduce(
        selectedNotificationUsers,
        function(collection, user) {
          collection.push(user.id);
          return collection;
        },
        []
      );
      const data = {
        notificationUsers: notificationUsers
      };
      this.notificationUsers = notificationUsers;
      InterviewsService.updateInterview(this.interview.id, data).then(() => {
        this.loadingNotificationUsers = false;
        this.editNotificationUsers = false;
      });
    },
    triggerEditName() {
      this.editName = true;
    },
    cancelUpdateName() {
      this.editName = false;
      this.interviewName = this.interview.name;
    },
    updateName() {
      const data = {
        name: this.interviewName
      };
      InterviewsService.updateInterview(this.interview.id, data).then(() => {
        this.editName = false;
      });
    },
    toggleCallToActionButton() {
      this.shouldShowCallToActionButton = !this.shouldShowCallToActionButton;
      if (this.shouldShowCallToActionButton === false) {
        this.nextStepsButtonLabel = "";
        this.nextStepsButtonUrl = "";
      }
    },
    scheduleUrl(keyword) {
      return InterviewsService.url(this.practice.subdomain, keyword);
    },
    canEdit() {
      let edit = false;
      if (this.interview.status === "draft" && this.id !== "published") {
        edit = true;
      }
      return edit;
    },
    showPublishButton() {
      return this.practice.subdomain !== "public";
    },
    showAddQuestion(id) {
      const page = _.find(Object.values(this.interview.pages), ["id", id]);
      this.page = page;
      this.$store.dispatch("interview/toggleAddQuestion", true);
    },
    addAlternateEnding() {
      InterviewsService.addAlternateEnding(this.interview.id);
    },
    addPage() {
      InterviewsService.addPage(this.interview.id);
    },
    deletePage(pageId) {
      InterviewsService.deletePage(this.interview.id, pageId);
    },
    showEditPageTrigger(id) {
      this.pageMenu = null;
      this.pageEdit = id;
    },
    showPageEdit(id) {
      return id === this.pageEdit;
    },
    hidePageEdit() {
      this.pageEdit = null;
    },
    pageLogicTrigger(id) {
      this.pageLogic = this.pageLogic ? null : id;
    },
    showPageLogic(id) {
      return id === this.pageLogic;
    },
    getInterview(id) {
      this.loading = true;
      InterviewsService.get(id).then(response => {
        this.loading = false;
        this.interviewName = this.interview.name;
        this.notificationUsers = this.interview.notificationUsers;
        let practiceId = _.get(response, "data.data.attributes.practice-id");
        if (practiceId) {
          PracticesService.getUsers(practiceId);
        }
      });
    },
    editInterview() {
      this.loading = true;
      this.$store.dispatch("interview/clearOpenEdit");
      InterviewsService.getDraftId(this.interview.id).then(id => {
        this.$router.push({ name: "interview-edit", params: { id } });
        this.loading = false;
      });
    },
    cancelInterviewEdit() {
      window.bus.$emit("interview:edit:cancel");

      if (this.practice.subdomain === "public") {
        this.$router.push({ name: "interview-pool" });
      } else {
        this.$router.go(-1);
      }
    },
    publish() {
      const openEditCount = this.$store.getters["interview/openEditCount"];
      if (openEditCount) {
        this.$refs.toastr.e(
          `Sorry, we cannot publish the interview, as there are ${openEditCount} pending updates`
        );
      } else {
        this.loading = true;
        InterviewsService.publish(this.interview.id).then(() => {
          this.loading = false;
        });
      }
    },
    toggleEditNextSteps() {
      this.nextSteps = this.interview.nextSteps;
      this.nextStepsHeader = this.interview.nextStepsHeader;
      this.nextStepsButtonLabel = this.interview.nextStepsButtonLabel;
      this.nextStepsButtonUrl = this.interview.nextStepsButtonUrl;
      this.editNextSteps = !this.editNextSteps;
    },
    saveNextSteps() {
      this.loadingNextSteps = true;
      const data = {
        nextStepsHeader: this.nextStepsHeader,
        nextSteps: this.nextSteps,
        nextStepsButtonLabel: this.nextStepsButtonLabel,
        nextStepsButtonUrl: this.nextStepsButtonUrl
      };
      InterviewsService.updateInterview(this.interview.id, data).then(() => {
        this.loadingNextSteps = false;
        this.editNextSteps = false;
      });
    },
    cloneInterview() {
      if (this.interview.kind === "PHQ_9") {
        this.$refs.toastr.e(
          "PHQ-9 interviews cannot be cloned. There should only be one master template."
        );
      } else {
        this.cloneLoading = true;
        PracticesService.cloneInterview({
          practiceId: this.practice.id,
          interviewId: this.interview.id
        }).then(() => {
          this.$refs.toastr.s("An interview template has been created.");
          this.cloneLoading = false;
        });
      }
    },
    toggleEditNextButton() {
      this.nextButtonText = this.interview.nextButtonText;
      this.editNextButton = !this.editNextButton;
    },
    saveNextButton() {
      this.loadingNextButton = true;
      const data = {
        nextButtonText: this.nextButtonText
      };
      InterviewsService.updateInterview(this.interview.id, data).then(() => {
        this.loadingNextButton = false;
        this.editNextButton = false;
      });
    },
    toggleEditButton() {
      this.startButtonIcon = this.interview.startButtonIcon;
      this.startButtonText = this.interview.startButtonText;
      this.editButton = !this.editButton;
    },
    saveButton() {
      this.loadingButton = true;
      const data = {
        startButtonIcon: this.startButtonIcon,
        startButtonText: this.startButtonText
      };
      InterviewsService.updateInterview(this.interview.id, data).then(() => {
        this.loadingButton = false;
        this.editButton = false;
      });
    },
    toggleEditWelcome() {
      this.welcomeHeader = this.interview.welcomeHeader;
      this.welcomeMessage = this.interview.welcomeMessage;
      this.editWelcome = !this.editWelcome;
    },
    saveWelcome() {
      this.loadingWelcome = true;
      const data = {
        welcomeHeader: this.welcomeHeader,
        welcomeMessage: this.welcomeMessage
      };
      InterviewsService.updateInterview(this.interview.id, data).then(() => {
        this.loadingWelcome = false;
        this.editWelcome = false;
      });
    },

    toggleEditUserTokenExpiredMessage() {
      this.welcomeHeader = this.interview.welcomeHeader;
      this.userTokenExpiredMessage = this.interview.userTokenExpiredMessage;
      this.editUserTokenExpiredMessage = !this.editUserTokenExpiredMessage;
    },
    saveUserTokenExpiredMessage() {
      this.loadingUserTokenExpiredMessage = true;
      const data = {
        userTokenExpiredMessage: this.userTokenExpiredMessage
      };
      InterviewsService.updateInterview(this.interview.id, data).then(() => {
        this.loadingUserTokenExpiredMessage = false;
        this.editUserTokenExpiredMessage = false;
      });
    },
    toggleEditSchedule() {
      this.scheduleMessage = this.interview.scheduleMessage;
      this.editSchedule = !this.editSchedule;
    },
    saveSchedule() {
      this.loadingSchedule = true;
      const data = { scheduleMessage: this.scheduleMessage };
      InterviewsService.updateInterview(this.interview.id, data).then(() => {
        this.loadingSchedule = false;
        this.editSchedule = false;
      });
    },
    setTab(tab) {
      this.tab = tab;
    },
    toggleEditNotificationEmail() {
      this.notificationEmailSubject = this.interview.notificationEmailSubject;
      this.notificationEmailBody = this.interview.notificationEmailBody;
      this.editNotificationEmail = !this.editNotificationEmail;
    },
    saveNotificationEmail() {
      this.loadingNotificationEmail = true;
      const data = {
        notificationEmailSubject: this.notificationEmailSubject,
        notificationEmailBody: this.notificationEmailBody
      };
      InterviewsService.updateInterview(this.interview.id, data).then(() => {
        this.loadingNotificationEmail = false;
        this.editNotificationEmail = false;
      });
    },
    toggleEditNotificationText() {
      this.notificationText = this.interview.notificationText;
      this.editNotificationText = !this.editNotificationText;
    },
    saveNotificationText() {
      this.loadingNotificationText = true;
      const data = { notificationText: this.notificationText };
      InterviewsService.updateInterview(this.interview.id, data).then(() => {
        this.loadingNotificationText = false;
        this.editNotificationText = false;
      });
    },
    toggleEditReferralEmail() {
      this.notificationReferrerSubject = this.interview.notificationReferrerSubject;
      this.notificationReferrerBody = this.interview.notificationReferrerBody;
      this.editReferralEmail = !this.editReferralEmail;
    },
    saveReferralEmail() {
      this.loadingReferralEmail = true;
      const data = {
        notificationReferrerSubject: this.notificationReferrerSubject,
        notificationReferrerBody: this.notificationReferrerBody
      };
      InterviewsService.updateInterview(this.interview.id, data).then(() => {
        this.loadingReferralEmail = false;
        this.editReferralEmail = false;
      });
    },

    toggleEditOnCompletion() {
      this.submitToVisuwellOnCompletion = this.interview.submitToVisuwellOnCompletion;
      this.downSampleImagesWhenSendingToVisuwell = this.interview.downSampleImagesWhenSendingToVisuwell;
      this.editOnCompletion = !this.editOnCompletion;
    },
    saveOnCompletion() {
      this.loadingOnCompletion = true;
      const data = {
        submitToVisuwellOnCompletion: this.submitToVisuwellOnCompletion,
        downSampleImagesWhenSendingToVisuwell: this
          .downSampleImagesWhenSendingToVisuwell
      };
      InterviewsService.updateInterview(this.interview.id, data).then(() => {
        this.loadingOnCompletion = false;
        this.editOnCompletion = false;
      });
    },

    toggleEditRequireUserToken() {
      this.requireUserToken = this.interview.requireUserToken;
      this.editRequireUserToken = !this.editRequireUserToken;
    },
    saveRequireUserToken() {
      this.loadingRequireUserToken = true;
      const data = {
        requireUserToken: this.requireUserToken
      };
      InterviewsService.updateInterview(this.interview.id, data).then(() => {
        this.loadingRequireUserToken = false;
        this.editRequireUserToken = false;
      });
    },
    sectionClasses(additionalClasses) {
      let defaultClasses =
        "section interview-question-wrap column is-full-mobile" +
        "is-full-tablet is-full-desktop is-two-thirds-widescreen";
      return defaultClasses + additionalClasses;
    }
  },

  watch: {
    id(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.getInterview(newVal);
      }
    },
    scheduleMessage(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.scheduleMessageCount = newValue.length;
      }
    }
  },

  mounted() {
    if (this.$props.id) {
      // this.$store.dispatch("interview/reset");

      this.getInterview(this.$props.id);
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
.section {
  border: 1px solid #000;
  background: #fff;
  margin-top: 1rem;
}
small {
  font-size: 1rem;
}
.edit-buttons {
  width: 300px;
}
.fa-small {
  font-size: 95%;
  margin-top: 0.75rem;
}
.edit-button-icon {
  margin-top: 0.7rem;
}
pre {
  background: #fff;
}
.edit-name {
  top: -20px;
  position: relative;
  font-size: 20px;
}

.button {
  &.cancel {
    margin-left: 5px;
    margin-top: 10px;
  }
  &.update {
    margin-top: 10px;
  }
}
</style>
